// One link. No account, no install, no cookie banner, nothing to accept.
//
// Three states — before, during, after — and it has to be obvious which
// one you are in without reading carefully. That is what the headline is
// for: it is the only large thing on the screen, and it is a different
// colour and a different sentence in each state.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BASEMAP_LEAVES,
  createWayfindSession,
  loadEngine,
  readCapability,
  startDemoRun,
  type DemoRun,
  type DemoScenario,
  type FollowHandle,
  type FollowSnapshot,
  type Place,
  type RunPlan,
  type WayfindSession,
  THREAD_STATE
} from "@wayfind/core";
// The mark itself, from the brand package. The one-turn variant, because
// it is set here at wordmark cap height and the two-turn way needs 24px.
import markUrl from "@wayfind/brand/assets/favicon.svg";
import { config } from "./config";
import { useStore } from "./useStore";
import { Headline } from "./components/Headline";
import { StopCounter, StopThread } from "./components/StopThread";
import { Chips, Provenance } from "./components/Provenance";
import { MyLeg, NoPosition, Trace } from "./components/Trace";
import { MapSearch, readAnchor } from "./components/MapSearch";
import {
  MapView,
  sketchSpanLat,
  type MapMarker,
  type MapRoad,
  type MapViewport
} from "./components/MapView";
import { DemoPicker } from "./components/DemoPicker";
import { headlineFor } from "./words";

type Theme = "system" | "light" | "dark";

export function App(): JSX.Element {
  const [session, setSession] = useState<WayfindSession | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [follow, setFollow] = useState<FollowHandle | null>(null);
  const [demo, setDemo] = useState<DemoRun | null>(null);
  const [starting, setStarting] = useState<string | null>(null);
  const [geometry, setGeometry] = useState<[number, number][]>([]);
  const [theme, setTheme] = useState<Theme>("system");
  const [pickedPlace, setPickedPlace] = useState<Place | null>(null);
  const [roads, setRoads] = useState<MapRoad[]>([]);
  // Where the user dragged the map to. Null is follow mode: the view
  // tracks the run, the pin, or the anchor, exactly as before anybody
  // touched it. Set, it wins over all of those until "Recentre".
  const [view, setView] = useState<MapViewport | null>(null);
  const started = useRef(false);

  const capability = useMemo(() => readCapability(location.href), []);
  const sessionState = useStore(session?.store ?? null);
  const snapshot = useStore(follow?.store ?? null);

  useEffect(() => {
    if (theme === "system") document.documentElement.removeAttribute("data-theme");
    else document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Open the engine once. A missing route graph is not a failure here: the
  // session opens without one and says so, and the app has a state for it.
  useEffect(() => {
    // One session per tab. The ref is the guard rather than the effect's
    // own cleanup, because StrictMode runs this twice in development and a
    // cleanup that tore the first one down would leave the second early
    // return holding nothing — a page stuck on "Opening…" forever.
    if (started.current) return;
    started.current = true;
    // The `?b=` hint has to be read with the **engine's** parser, which
    // lives in the bundle — `readCapability`'s default reads no hint at
    // all (link.ts), so the render-time `capability` above always carries
    // an empty `bootstrap` and must not be used for this. Without the
    // hint there is no peer to dial and the page can only ever replay its
    // own demo, so this load has to happen before the session opens.
    loadEngine(config.engineBaseUrl)
      .then(bundles => createWayfindSession({
        engineBaseUrl: config.engineBaseUrl,
        routeGraphUrl: config.routeGraphUrl || null,
        datasetId: config.datasetId || null,
        engineMode: config.routeProfile,
        // A location, never a capability — the capability stayed in the
        // fragment, which browsers do not transmit.
        //
        // The link's hint first, then the configured keeper. Order is the
        // policy: a hint names the operator's own seed for this job and
        // is the better answer where it exists; the default is what keeps
        // a link that carried no hint from opening onto a page that can
        // only ever replay its own demo.
        bootstrap: [
          ...bundles.threads.parseBootstrapHint(location.href),
          ...config.meshBootstrap
        ].filter((address, index, all) => all.indexOf(address) === index),
        id: "recipient"
      }))
      .then(setSession)
      .catch((error: unknown) => {
        setOpenError(error instanceof Error ? error.message : String(error));
      });
  }, []);

  // A real link follows the run it names. Everything after this point is
  // identical for a demo and for a courier three streets away.
  useEffect(() => {
    if (!session || capability.kind !== "present") return;
    const present = capability;
    let handle: FollowHandle | null = null;
    let live = true;
    session
      // `recipient` is the customer's own stop, read out of the fragment.
      // No plan will ever arrive — §20.8 keeps every other doorstep off
      // the wire and out of this link — but one door plus a live position
      // is enough for the §20.2 floor, which is the whole difference
      // between this page saying "not before 21:34" and "Not yet known".
      // The **capability**, not `location.href`. The engine does its own
      // fragment parsing and would base64-decode the whole thing, card and
      // separator included, and throw — which is a link that opens nothing
      // at all rather than one that merely cannot say when. `readCapability`
      // has already split the two halves; this is the half the engine owns.
      .follow(present.fragment, {
        plan: null,
        myStopIndex: null,
        recipient: present.recipient
      })
      .then(opened => {
        if (!live) { opened.stop(); return; }
        handle = opened;
        setFollow(opened);
      })
      .catch((error: unknown) => {
        setOpenError(error instanceof Error ? error.message : String(error));
      });
    return () => {
      live = false;
      handle?.stop();
    };
  }, [session, capability.kind]);

  const startDemo = useCallback(async (scenario: DemoScenario) => {
    if (!session) return;
    setStarting(scenario.id);
    try {
      const run = await startDemoRun(session, scenario);
      const handle = await session.follow(run.link, {
        plan: run.plan,
        myStopIndex: run.myStopIndex,
        demo: true
      });
      setDemo(run);
      setFollow(handle);
    } catch (error) {
      setOpenError(error instanceof Error ? error.message : String(error));
    } finally {
      setStarting(null);
    }
  }, [session]);

  // The road the run will drive, unpacked from this device's own graph —
  // the same geometry the arrival time is computed over.
  const plan: RunPlan | null = snapshot?.plan ?? null;
  useEffect(() => {
    const engine = session?.engine;
    if (!engine || !plan || plan.stops.length < 2) { setGeometry([]); return; }
    let live = true;
    (async () => {
      const points: [number, number][] = [];
      for (let i = 0; i + 1 < plan.stops.length; i++) {
        const from = plan.stops[i]!;
        const to = plan.stops[i + 1]!;
        const leg = await engine.route({ from, to, geometry: true }).catch(() => null);
        for (const point of leg?.geometry ?? [[from.lat, from.lon], [to.lat, to.lon]]) {
          points.push(point as [number, number]);
        }
      }
      if (live) setGeometry(points);
    })();
    return () => { live = false; };
  }, [session, plan]);

  useEffect(() => () => { void demo?.stop(); }, [demo]);

  // The basemap. Real streets, out of this device's own route graph — the
  // same polylines the router walks — so there is no tile server, no
  // account, and no way to draw a road the arrival time does not believe
  // in. Refetched when what is being looked at moves, and bounded by the
  // engine so a zoomed-out view cannot ask for a country.
  //
  // Falling back to the remembered anchor is what makes the page open on
  // a map at all. Without it a first visit has no run and no pin, so
  // there is nothing to centre on and the ground is blank — which is the
  // one thing a map-first page cannot be. `mapHome` is the last place
  // this browser looked something up, and failing that the area the
  // configured route graph actually covers: there is no world graph, so
  // the honest default is the middle of the map this device holds.
  const graphHome = session?.engine?.root
    ? homeOfGraph(session.engine.root as { leaves?: { bbox?: Record<string, number> }[] })
    : null;
  const focus = snapshot?.recipient
    ? { lat: snapshot.recipient.lat, lon: snapshot.recipient.lon }
    : snapshot?.position
      ? { lat: snapshot.position.lat, lon: snapshot.position.lon }
      : pickedPlace && pickedPlace.lat != null && pickedPlace.lon != null
        ? { lat: pickedPlace.lat, lon: pickedPlace.lon }
        : readAnchor() ?? graphHome;
  // Roads are fetched for wherever is being looked at — which is the
  // user's own view once they have dragged, not the run's. Without that,
  // panning two blocks walked straight off the edge of the fetched
  // streets onto blank ground.
  const mapCentre = view?.centre ?? focus;
  const mapPad = view ? Math.max(0.014, sketchSpanLat(view.zoom) * 0.8) : 0.014;
  useEffect(() => {
    const engine = session?.engine;
    if (!engine || !mapCentre) { setRoads([]); return; }
    let live = true;
    // About 3 km across by default: close enough that streets are
    // legible, wide enough that a van a few blocks away is on the same
    // picture. Zoomed out, the pad follows the view; the engine bounds
    // the request, so a wide view cannot ask for a country.
    const pad = mapPad;
    void engine
      .roadsIn({
        minLat: mapCentre.lat - pad, maxLat: mapCentre.lat + pad,
        minLon: mapCentre.lon - pad * 1.6, maxLon: mapCentre.lon + pad * 1.6
      }, { maxLeaves: BASEMAP_LEAVES })
      .then(result => { if (live) setRoads(result.roads as MapRoad[]); })
      .catch(() => { if (live) setRoads([]); });
    return () => { live = false; };
  }, [session, mapCentre?.lat, mapCentre?.lon, mapPad]);

  // Everything the map should show, derived from the one snapshot rather
  // than assembled per component — a marker that could be built from
  // anywhere is a marker that can outlive the claim it stands for.
  const markers: MapMarker[] = [];
  if (snapshot?.position) {
    markers.push({ id: "van", kind: "live", lat: snapshot.position.lat, lon: snapshot.position.lon, label: "The vehicle, now" });
  }
  if (snapshot?.recipient) {
    markers.push({ id: "mine", kind: "mine", lat: snapshot.recipient.lat, lon: snapshot.recipient.lon, label: "Your address" });
  }
  for (const stop of snapshot?.plan?.stops ?? []) {
    if (stop.index === snapshot?.myStopIndex) continue;
    markers.push({ id: `stop-${stop.index}`, kind: "stop", lat: stop.lat, lon: stop.lon });
  }
  if (pickedPlace && pickedPlace.lat != null && pickedPlace.lon != null) {
    markers.push({ id: "picked", kind: "place", lat: pickedPlace.lat, lon: pickedPlace.lon, label: pickedPlace.label });
  }
  // What the search should bias toward: the run if there is one, then
  // whatever was last looked up. Without an anchor a prefix fans out
  // across every shard on Earth (see MapSearch).
  const searchNear = snapshot?.recipient
    ? { lat: snapshot.recipient.lat, lon: snapshot.recipient.lon }
    : snapshot?.position
      ? { lat: snapshot.position.lat, lon: snapshot.position.lon }
      : pickedPlace && pickedPlace.lat != null && pickedPlace.lon != null
        ? { lat: pickedPlace.lat, lon: pickedPlace.lon }
        : null;

  return (
    <div className="shell shell--map">
      <div className="shell__inner shell__inner--map">
        <header className="topbar">
          <span className="wordmark">
            <img className="wordmark__mark" src={markUrl} alt="" width="24" height="24" />
            Wayfind
          </span>
          <button
            type="button"
            className="ghost-button"
            onClick={() => setTheme(current => (current === "dark" ? "light" : "dark"))}
            aria-label={`Switch to ${theme === "dark" ? "day" : "night"} colours`}
          >
            {theme === "dark" ? "Day" : "Night"}
          </button>
        </header>

        <div className="mapstage">
          <div className="mapstage__ground">
            <MapView
              markers={markers}
              roads={roads}
              extent={focus ? {
                minLat: focus.lat - 0.011, maxLat: focus.lat + 0.011,
                minLon: focus.lon - 0.011 * 1.9, maxLon: focus.lon + 0.011 * 1.9
              } : null}
              road={snapshot?.legGeometry?.length ? snapshot.legGeometry : geometry}
              viewport={view}
              onViewportChange={setView}
              styleUrl={config.mapStyleUrl || null}
            />
          </div>

          {/*
            Search is for a page opened with nothing to follow — finding
            your own street, poking at the demo. A customer holding a real
            link came here to watch one vehicle reach one door, and a
            search box floating over that reads as a form they are
            supposed to fill in. It also cannot help them: the run's
            addresses are fixed by the capability, not typed.
          */}
          {capability.kind !== "present" ? (
            <MapSearch
              near={searchNear}
              picked={pickedPlace}
              onPick={setPickedPlace}
              onClear={() => setPickedPlace(null)}
            />
          ) : null}

          <div className="sheet">
        {snapshot?.status.demo ? <DemoBanner run={demo} /> : null}

        {openError ? (
          <section className="panel">
            <p className="eyebrow">Something is wrong here</p>
            <p style={{ margin: 0 }}>{openError}</p>
          </section>
        ) : null}

        {capability.kind === "unreadable" ? (
          <section className="panel">
            <p className="eyebrow">That link is not complete</p>
            <p style={{ margin: 0, color: "var(--ink-soft)" }}>{capability.reason}</p>
          </section>
        ) : null}

        {!session && !openError ? <Opening /> : null}

        {sessionState && sessionState.map.kind === "none" ? (
          <NoPosition
            title="No routing map for this area"
            body={
              sessionState.map.reason === "unreachable"
                ? "A map was configured but could not be read, so nothing on this page will carry an "
                  + "arrival time. Whoever sent the link can point it at a route graph for the region."
                : "Route graphs are published region by region and this page has not been given one. "
                  + "Everything a run reports about its stops still works; an arrival time does not, "
                  + "and none will be invented."
            }
          />
        ) : null}

        {session && !follow && capability.kind !== "present" && sessionState?.map.kind === "graph" ? (
          <DemoPicker
            onPick={scenario => { void startDemo(scenario); }}
            busy={starting}
            ready={session != null}
          />
        ) : null}

        {session && !follow && capability.kind === "present" && !openError ? <Opening /> : null}

        {snapshot ? (
          <Run snapshot={snapshot} geometry={geometry} />
        ) : null}

          </div>
        </div>

        <footer className="footer footer--map">
          <p>
            The arrival time on this page is worked out on this device, from the run’s own
            broadcast. Nobody was told which stop you are waiting at.
          </p>
          <p>No account. No cookie. The link in your address bar is never sent anywhere.</p>
        </footer>
      </div>
    </div>
  );
}

function Opening(): JSX.Element {
  return (
    <section className="panel">
      <p className="row-quiet">
        <span className="spinner" aria-hidden="true" />
        Opening…
      </p>
    </section>
  );
}

function DemoBanner({ run }: { run: DemoRun | null }): JSX.Element {
  return (
    <div className="banner" role="note">
      <span className="banner__mark">Demo</span>
      <p className="banner__text">
        <b>This is a demonstration, not a real delivery.</b>{" "}
        A publisher in this tab is driving {run ? `“${run.scenario.title.toLowerCase()}”` : "a plan"} over
        the loopback transport. The records, the signatures and the arrival time are real; the van is not.
      </p>
    </div>
  );
}

/**
 * Before, during, after — decided by the run itself, and legible from
 * across a kitchen.
 */
function Run({
  snapshot,
  geometry
}: {
  snapshot: FollowSnapshot;
  geometry: [number, number][];
}): JSX.Element {
  const { status, arrival, position, plan, myStopIndex, recipient, legGeometry } = snapshot;
  const headline = headlineFor(arrival, status, myStopIndex);
  const coarse = status.position.kind === "withheld";
  const showTrace = plan != null && plan.stops.length > 1 && !coarse && status.map.kind === "graph";
  // A real dispatched recipient has no plan and never will, so `Trace`
  // has nothing to draw for them. What they do have is their own door and
  // the leg the arrival was measured along — which is a map worth having,
  // and until now was the difference between this page showing the run
  // and showing a paragraph about it.
  const showMyLeg = !showTrace && recipient != null && !coarse && status.map.kind === "graph";

  return (
    <>
      <section className="panel">
        <PhaseLabel
          phase={status.phase}
          coarse={coarse}
          settled={arrival.kind === "served" || arrival.kind === "not-coming"}
          running={status.state !== THREAD_STATE.COMPLETED && status.state !== THREAD_STATE.CANCELED}
        />
        <Headline copy={headline} />
        <div style={{ marginTop: "0.9rem" }}>
          <Chips status={status} arrival={arrival} />
        </div>
      </section>

      {coarse ? (
        <NoPosition
          title="This run does not publish a position"
          body={
            "You get the stops, not the vehicle. A link like this one is worth about what a printed "
            + "timetable is worth if it is forwarded — which is the point of sending it this way."
          }
        />
      ) : null}

      {status.position.kind === "unplaceable" ? (
        <NoPosition
          title="No map of this area on this device"
          body={
            "The driver is publishing a position right now. This page has no route graph for the "
            + "region, so it cannot place that position or work out an arrival time. Everything the "
            + "run reports about the stops is still below and still true."
          }
        />
      ) : null}

      {status.position.kind === "stale" ? (
        <NoPosition
          title="The position is out of date"
          body={
            `Nothing has arrived from the run for ${Math.round(status.position.ageSeconds / 60) || 1} `
            + "minute(s). Rather than leave the last one on screen looking live, it is taken down."
          }
        />
      ) : null}

      {showMyLeg && recipient ? (
        <section className="panel panel--flush">
          <MyLeg
            geometry={legGeometry}
            position={position}
            door={{ lat: recipient.lat, lon: recipient.lon }}
            settled={arrival.kind === "served" || arrival.kind === "not-coming"}
          />
          <p className="trace-caption">
            {position
              ? "Your address, and where the driver is now. The road drawn between them is the one "
                + "the time above was measured along — not the way the van will actually come, "
                + "because the stops in between are not in this link."
              : "Your address. Nothing is being published about where the driver is."}
          </p>
        </section>
      ) : null}

      {showTrace && plan ? (
        <section className="panel panel--flush">
          <Trace
            plan={plan}
            geometry={geometry}
            status={status}
            position={position}
            myStopIndex={myStopIndex}
          />
          <p className="trace-caption">
            {position
              ? "The road this run is driving, and where the driver is on it."
              : "The road this run is driving. No position is being drawn."}
          </p>
        </section>
      ) : null}

      {coarse ? (
        <section className="panel">
          <StopCounter plan={plan} status={status} />
        </section>
      ) : null}

      {plan ? (
        <section className="panel">
          <p className="eyebrow">{coarse ? "The route" : "The round"}</p>
          <StopThread plan={plan} status={status} myStopIndex={myStopIndex} coarse={coarse} />
        </section>
      ) : null}

      {status.lastOutcome?.hasPhoto ? <ProofPanel chainHead={status.photoChain} /> : null}

      {status.lastOutcome && status.lastOutcome.stopIndex !== myStopIndex ? (
        <section className="panel">
          <div className="note">
            <p className="note__title">Latest from the {coarse ? "route" : "round"}</p>
            <p className="note__body">
              Stop {status.lastOutcome.stopIndex} was{" "}
              {coarse
                ? (status.lastOutcome.outcome === 1 ? "cleared" : "not called at")
                : `marked ${status.lastOutcome.outcome === 1 ? "delivered" : status.lastOutcome.outcome === 2 ? "skipped" : "not delivered"}`}.
            </p>
          </div>
        </section>
      ) : null}

      <Provenance status={status} arrival={arrival} />
    </>
  );
}

function PhaseLabel({
  phase,
  coarse,
  settled,
  running
}: {
  phase: "before" | "during" | "after";
  coarse: boolean;
  /** This viewer's own stop has an outcome on it. */
  settled: boolean;
  /** The vehicle is still working its way down the plan. */
  running: boolean;
}): JSX.Element {
  // "After" is reached two ways and they are not the same sentence. A
  // round that has finished is over for everyone; a stop that has been
  // marked is over for *you*, while the van carries on down the street.
  const label = phase === "before"
    ? "Not started yet"
    : phase === "after"
      ? (settled && running ? "Your stop is settled" : "This round is done")
      : coarse ? "On the route" : "On the way";
  return <p className="eyebrow">{label}</p>;
}

/**
 * §20.7 — a customer holds the 78-byte link, which is a capability
 * secret, `P`, an epoch prefix and an expiry. A photo's key derives from
 * the run's **private seed**,
 * which is not in the link and is not derivable from it. So this page says
 * a photo exists and does not attempt the fetch: pretending otherwise
 * would be a spinner that can never finish.
 *
 * The wire says *that* a photo exists with one signed boolean; *which*
 * photos is §20.7.1's accumulator, and its head is the code shown here.
 * An earlier version waited for a per-stop hash that never arrives, so a
 * driver could photograph every doorstep and this panel stayed blank.
 */
function ProofPanel({ chainHead }: { chainHead: string | null }): JSX.Element {
  return (
    <section className="panel">
      <p className="eyebrow">Proof of delivery</p>
      <div className="note">
        <p className="note__title">A photo was taken at the door.</p>
        <p className="note__body">
          It is committed to inside the driver’s signed record, so it cannot be swapped or removed
          afterwards. Opening it needs a key this link does not carry — ask whoever sent you here.
          {chainHead ? (
            <>
              <br />
              {/* A commitment somebody may well read down a phone to whoever
                  sent the link, so it is set in Plex Mono: 0/O and 1/l have to
                  be distinct or the comparison is a coin flip. */}
              <span className="wf-code note__code">{chainHead.slice(0, 16)}…</span>
            </>
          ) : null}
        </p>
      </div>
    </section>
  );
}


/**
 * The middle of the area this device holds a map for.
 *
 * There is no world route graph — they are published region by region —
 * so "where should the map open" has a real answer only when one has been
 * configured, and this is it.
 *
 * Derived from the **leaves**, because the root has no bbox of its own.
 * An earlier version read `root.bbox` and silently got `undefined`, so
 * every cold visitor landed on a blank map: no bbox, no home, no focus,
 * no roads fetched. It looked fine in development purely because
 * localStorage already held an anchor from testing — which is exactly the
 * kind of bug that only a real first visit finds.
 *
 * Returns null rather than a guess when there are no leaves, because a map
 * centred on nowhere in particular is worse than one that admits it has
 * nothing to show.
 */
function homeOfGraph(
  root: { leaves?: { bbox?: Record<string, number> }[] }
): { lat: number; lon: number } | null {
  const leaves = root.leaves ?? [];
  let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
  for (const leaf of leaves) {
    const box = leaf?.bbox;
    if (!box) continue;
    // A leaf missing any side is not a usable box; skipping beats
    // treating an absent edge as zero, which would drag the centre into
    // the Gulf of Guinea.
    const lo = Number(box.minLat), hi = Number(box.maxLat);
    const west = Number(box.minLon), east = Number(box.maxLon);
    if (![lo, hi, west, east].every(Number.isFinite)) continue;
    if (lo < minLat) minLat = lo;
    if (hi > maxLat) maxLat = hi;
    if (west < minLon) minLon = west;
    if (east > maxLon) maxLon = east;
  }
  if (!Number.isFinite(minLat) || !Number.isFinite(minLon)) return null;
  // Leaf bboxes are E7 integers, like everything else geographic here.
  const scale = Math.abs(maxLat) > 1000 ? 1e7 : 1;
  return { lat: (minLat + maxLat) / 2 / scale, lon: (minLon + maxLon) / 2 / scale };
}
